import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ScrollBtn } from "./ScrollToTop.styles";

// ─── Icon ─────────────────────────────────────────────────────────────────────
const ChevronUp = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
const THRESHOLD = 280;

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  const pulsedRef = useRef(false);

  const location = useLocation();

  // ───────────────────────────────────────────────────────────────────────────
  // Scroll to top whenever the React Router route changes
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    // Reset the floating button state for the new page
    setVisible(false);
    pulsedRef.current = false;
  }, [location.pathname, location.search]);

  // ───────────────────────────────────────────────────────────────────────────
  // Show/hide floating back-to-top button
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        const show = window.scrollY > THRESHOLD;

        setVisible(show);

        if (show) {
          pulsedRef.current = true;
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Floating button click
  // ───────────────────────────────────────────────────────────────────────────
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <ScrollBtn
      type="button"
      $visible={visible}
      $pulsed={pulsedRef.current}
      onClick={scrollToTop}
      aria-label="Back to top"
      tabIndex={visible ? 0 : -1}
    >
      <ChevronUp />
    </ScrollBtn>
  );
};

ScrollToTop.displayName = "ScrollToTop";

export default React.memo(ScrollToTop);